import { inject, signal, Service } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { exhaustMap, map, Observable, of, pipe, switchMap, tap } from 'rxjs';

import { ToastService } from '@ideals/components/toast';
import { TimeZoneService } from '@ideals/services/time-zone';
import { getDomain } from '@ideals/utils/get-domain';
import { rxMethod } from '@ideals/utils/rx-method';
import { tapResponse } from '@ideals/utils/tap-response';

import { RequestError, UserAccount } from '../../../../data/models';
import { ErrorsService } from '../../../../data/services/errors';
import { AuthUser, UpdateAuthUser } from '../../models';
import { AuthUserService } from '../../services/auth-user';

@Service()
export class AuthUserStore {
  readonly #authUserService = inject(AuthUserService);
  readonly #cookieService = inject(CookieService);
  readonly #errorsService = inject(ErrorsService);
  readonly #timeZoneService = inject(TimeZoneService);
  readonly #toastService = inject(ToastService);
  readonly #translateService = inject(TranslateService);

  readonly loading = signal(true);
  readonly user = signal<AuthUser | undefined>(undefined);

  readonly loadAuthUser = rxMethod<void>(
    pipe(
      tap(() => this.loading.set(true)),
      exhaustMap(() => this.#authUserService.loadAuthUser().pipe(
        tapResponse({
          next: (user) => {
            this.user.set(user);
            this.loading.set(false);
          },
          error: (error: RequestError) => {
            this.#errorsService.handleError(error);
            this.loading.set(false);
          },
        }),
      )),
    ),
  );

  addAuthUserAccount(account: UserAccount): void {
    this.user.update((user) => user && {
      ...user,
      accounts: [...user.accounts, account],
    });
  }

  updateAuthUser(user: UpdateAuthUser): Observable<void> {
    return this.#authUserService.updateAuthUser(user).pipe(
      switchMap((updatedAuthUser) => {
        const currentAuthUser = this.user();

        if (updatedAuthUser.culture !== currentAuthUser!.culture) {
          this.#cookieService.set('culture', updatedAuthUser.culture, { path: '/', domain: getDomain() });

          return this.#translateService.use(updatedAuthUser.culture).pipe(map(() => updatedAuthUser));
        }

        return of(updatedAuthUser);
      }),
      map((updatedAuthUser) => {
        this.user.update((currentAuthUser) => currentAuthUser && { ...currentAuthUser, ...updatedAuthUser });
        this.#timeZoneService.updateTimezoneIfNeeded(updatedAuthUser.timeZone, updatedAuthUser.isTimeZoneAutoUpdated);
        this.#toastService.success('personal_info.SUCCESS.changed_personal_info');
      }),
    );
  }
}
